import '@naturalcycles/ncuikit/dist/Button.css'
import { Button, ButtonVariant } from '@naturalcycles/ncuikit'
import { Checkbox } from '@src/components/checkbox/Checkbox.component'
import { InputNc } from '@src/components/input-nc/InputNc.component'
import { SignupButton } from '@src/components/signup-button/SignupButton.component'
import { checkValidation, ValidationType } from '@src/helpers/checkValidation'
import { useAppDispatch } from '@src/hooks/useAppDispatch'
import { mixpanelService } from '@src/srv/mixpanel.service'
import { newsletterService } from '@src/srv/newsletter.service'
import { setDiscountCode, setDiscountPercentage } from '@src/store/discountCode/discountCode.slice'
import classNames from 'classnames'
import {
  ChangeEvent,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Helmet } from 'react-helmet-async'
import styles from './PopupFixedAccordion.module.scss'

export interface PopupFixedAccordionProps {
  _modelApiKey: 'fixed_accordion_popup'
  isOpen: boolean
  handleDismiss: () => void
  name: string
  promoCode: string
  successTitle: string
  successText: string
  discountLine: string
  saveDiscountButton: string
  signupDiscountButton: string
  discountPercentage: string
  formTitle: string
  formText: string
  consent: string
  buttonText: string
}

enum PopupFixedAccordionSteps {
  Form = 'Form',
  Signup = 'Signup',
}

export function PopupFixedAccordion({
  name,
  successTitle,
  successText,
  promoCode,
  discountLine,
  saveDiscountButton,
  signupDiscountButton,
  discountPercentage,
  handleDismiss,
  formTitle,
  formText,
  consent,
  buttonText,
  isOpen,
}: PopupFixedAccordionProps): ReactNode {
  const [isConsentChecked, setIsConsentChecked] = useState(false)
  const [error, setError] = useState(false)
  const [email, setEmail] = useState('')
  const [step, setStep] = useState(PopupFixedAccordionSteps.Form)
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const dispatch = useAppDispatch()
  const icon = useRef<any>()
  const hasOpened = useRef(false)

  const onOpen = useCallback(() => {
    // Focusing an element inside the modal avoids the problem of the modal
    // appearing visually but going unnoticed by screen readers, whose focus
    // remains on the main page content behind, often with no clear path forward
    // for the user to rectify the situation.
    icon.current.focus()

    hasOpened.current = true

    mixpanelService.track('LeadCapturePopupImpression', {
      name,
    })
  }, [])

  useEffect(() => {
    if (isOpen) {
      onOpen()
    }
  }, [isOpen])

  const handleCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(event.target.checked)
  }, [])

  const handleSubmit = useCallback(() => {
    if (error) {
      return
    }

    if (!checkValidation(email, ValidationType.Email)) {
      return setError(true)
    }

    const consentTo = [formTitle, formText, buttonText].join('\n')

    newsletterService
      .addToNewsletterAccount({ email, source: 'Popup', campaign: name, consentTo })
      .then(() => processSuccess())
      .catch(() => processError())
  }, [email])

  const processError = useCallback(() => {
    setError(true)
    mixpanelService.track('LeadCapturePopupError', {
      name,
    })
  }, [])

  const processSuccess = useCallback(() => {
    setStep(PopupFixedAccordionSteps.Signup)
    mixpanelService.track('LeadCapturePopupSuccess', {
      name,
    })
  }, [email])

  const handleChange = useCallback((email: SetStateAction<string>) => {
    setError(false)
    setEmail(email)
  }, [])

  const handleSavingDiscountCode = useCallback(() => {
    if (promoCode) {
      dispatch(setDiscountCode(promoCode, 'LeadCapturePopup'))
    }
    dispatch(setDiscountPercentage(discountPercentage))
    mixpanelService.track('PopupLeadCaptureSaveDiscountCode', {
      name,
    })
    handleDismiss()
  }, [dispatch])

  const handleToggle = useCallback(() => {
    mixpanelService.track('LeadCapturePopupToggle', {
      name,
      isOpen: !isAccordionOpen,
    })
    setIsAccordionOpen(!isAccordionOpen)
  }, [isAccordionOpen])

  return (
    <>
      <Helmet>
        <body data-popup-type="PopupFixedAccordion" />
      </Helmet>
      <div
        className={classNames(styles.Accordion, { [styles.Accordion___isOpen]: isAccordionOpen })}
      >
        <div className={styles.Accordion__header} onClick={handleToggle}>
          <div className={styles.Accordion__title}>{formTitle}</div>
          <div className={styles.Accordion__icon} ref={icon}>
            <img src="/icons/ab381/chevron-up.png" alt="Open icon" loading="lazy" />
          </div>
        </div>
        <div className={styles.Accordion__content}>
          <div className={styles.Popup}>
            {step === PopupFixedAccordionSteps.Form && (
              <>
                <p>{formText}</p>
                <div className={styles.Popup__input}>
                  <InputNc
                    setValue={handleChange}
                    id="email-input"
                    type="email"
                    label="Email"
                    keyPressEvent={handleSubmit}
                    errorMessage="Your email is incorrect"
                    showErrorFromParent={error}
                    mixPanelServiceTrackName={name}
                    mixPanelTrackProp="LeadCapturePopupFocus"
                  />
                </div>
                <div className={styles.Popup__consent}>
                  <Checkbox
                    id="lead-capture-consent"
                    checked={isConsentChecked}
                    onChange={handleCheckboxChange}
                  >
                    {consent}
                  </Checkbox>
                </div>
                <div className={styles.Popup__submit}>
                  <Button isDisabled={!isConsentChecked} onClick={handleSubmit}>
                    {buttonText}
                  </Button>
                </div>
              </>
            )}
            {step === PopupFixedAccordionSteps.Signup && (
              <>
                <h1 className={styles.Popup__signupTitle}>{successTitle}</h1>
                <p
                  className={styles.Popup__signupText}
                  dangerouslySetInnerHTML={{ __html: successText }}
                />
                <p className={styles.Popup__signupCode}>
                  {discountLine} <b>{promoCode}</b>
                </p>
                <div
                  className={styles.Popup__buttons}
                  aria-label={`Sign up with discount code ${promoCode}`}
                >
                  <SignupButton code={promoCode}>{signupDiscountButton}</SignupButton>
                  <Button variant={ButtonVariant.SECONDARY} onClick={handleSavingDiscountCode}>
                    {saveDiscountButton}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
